import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../Aplazamiento/Aplazamiento.css';
import './RevisarAplazamiento.css';
import { useAuth } from '../../context/AuthContext';
import { useTemporada } from '../../context/TemporadaContext';
import {
  aplicarWo, aprobarJustificante, fijarFechaJuego, getSolicitudes, marcarRevisada, rechazarJustificante,
} from '../../services/revisarAplazamientoService';
import PartidoVs from '../Aplazamiento/PartidoVs';
import { formatearFecha } from '../MiZona/estadosPartido';
import ModalComunicar from './ModalComunicar';
import VisorJustificante from './VisorJustificante';

export const FILTROS = {
  todas: { etiqueta: 'Todas', pasa: () => true },
  justificantes: { etiqueta: 'Justificantes por aprobar', pasa: (s) => s.justificanteEstado === 'PENDIENTE' },
  pendientes: { etiqueta: 'Pendientes de revisar', pasa: (s) => !s.revisadaEn },
  wo: { etiqueta: 'Admiten WO', pasa: (s) => s.admiteWo },
  justificante: { etiqueta: 'Con justificante', pasa: (s) => s.tieneJustificante },
};

const fechaCorta = (iso) => formatearFecha(String(iso).slice(0, 10));
const horaCorta = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false });
};
const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });

const ETIQUETA_JUSTIFICANTE = {
  PENDIENTE: ['chip-danger', 'Justificante por aprobar'],
  APROBADO: ['chip-success', 'Justificante aprobado'],
  RECHAZADO: ['chip-warning', 'Justificante rechazado'],
};

// Pantalla /dashboard/revisar-aplazamiento (solo COMITE/ADMIN): solicitudes
// de aplazamiento con visor del justificante y acciones del comité.
export default function RevisarAplazamiento() {
  const { usuario } = useAuth();
  const { temporada } = useTemporada();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState(null);
  const [abierta, setAbierta] = useState(null);
  const [trabajando, setTrabajando] = useState(false);
  const [errorAccion, setErrorAccion] = useState(null);
  const [fechaElegida, setFechaElegida] = useState('');
  const [avisando, setAvisando] = useState(null);
  const esGestor = usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE';

  const cargar = useCallback(() =>
    getSolicitudes(temporada).then((d) => { setDatos(d); setError(null); }).catch(setError), [temporada]);

  useEffect(() => { if (esGestor) cargar(); }, [esGestor, cargar]);

  const porAprobar = useMemo(
    () => (datos ? datos.solicitudes.filter(FILTROS.justificantes.pasa).length : 0),
    [datos],
  );
  // Sin elección del usuario: lo urgente (justificantes por aprobar) o, si no hay, las pendientes.
  const filtroActivo = filtro ?? (porAprobar > 0 ? 'justificantes' : 'pendientes');
  const visibles = useMemo(
    () => (datos
      ? datos.solicitudes.filter(FILTROS[filtroActivo].pasa)
        .sort((a, b) => String(a.creadaEn).localeCompare(String(b.creadaEn)) || a.id - b.id)
      : []),
    [datos, filtroActivo],
  );

  const ejecutar = async (accion, id, ...args) => {
    setTrabajando(true);
    setErrorAccion(null);
    try {
      const r = await accion(id, ...args);
      setFechaElegida('');
      if (r?.vencido) {
        setErrorAccion(`Justificante rechazado, pero el plazo venció el ${formatearFecha(r.fechaLimite)}: aplica la incomparecencia si procede.`);
      }
      await cargar();
    } catch (err) {
      setErrorAccion(err.message || 'No se pudo completar la acción.');
    } finally {
      setTrabajando(false);
    }
  };

  if (!esGestor) {
    return <p className="msg-error mz-estado-pagina" role="alert">Solo el comité y el administrador pueden revisar aplazamientos.</p>;
  }
  if (error) return <p className="msg-error mz-estado-pagina" role="alert">{error.message || 'No se pudieron cargar las solicitudes.'}</p>;
  if (!datos) return <p className="mz-estado-pagina">Cargando…</p>;

  return (
    <div className="revisar-aplazamiento page-content">
      <header className="page-header">
        <h2>Revisar aplazamientos</h2>
        <span className="chip chip-muted">{datos.temporada.nombre}</span>
      </header>

      {porAprobar > 0 && (
        <p className="ap-banner ra-urgente" role="alert">
          <strong>{porAprobar} justificante{porAprobar === 1 ? '' : 's'} de aplazamiento por aprobar o rechazar.</strong>{' '}
          Hay que revisarlo{porAprobar === 1 ? '' : 's'} inmediatamente: hasta entonces el partido sigue pendiente.
        </p>
      )}

      <div className="ra-filtros" role="group" aria-label="Filtrar solicitudes">
        {Object.entries(FILTROS).map(([clave, f]) => (
          <button key={clave} type="button" aria-pressed={filtroActivo === clave}
            className={`btn btn-sm ${filtroActivo === clave ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltro(clave)}>
            {clave === 'justificantes' && porAprobar > 0 ? `${f.etiqueta} (${porAprobar})` : f.etiqueta}
          </button>
        ))}
      </div>

      {visibles.length === 0 ? <p className="mz-vacio">No hay solicitudes con este filtro.</p> : (
        <ul className="ap-lista">
          {visibles.map((s) => (
            <li key={s.id} className="ra-solicitud">
              <button type="button" className="ra-resumen" aria-expanded={abierta === s.id}
                onClick={() => { setAbierta((a) => (a === s.id ? null : s.id)); setErrorAccion(null); }}>
                <span className="ra-titulo">
                  <strong>{s.jugador}</strong>
                  <span className="ra-solicitada">Solicitada el {fechaCorta(s.creadaEn)} {horaCorta(s.creadaEn)}</span>
                </span>
                <span>Jornada {s.jornada} · {s.modalidad} · <PartidoVs partido={s} /></span>
                <span className="ra-etiquetas">
                  {s.admiteWo && <span className="chip chip-warning">Admite WO</span>}
                  {s.justificanteEstado
                    ? <span className={`chip ${ETIQUETA_JUSTIFICANTE[s.justificanteEstado][0]}`}>{ETIQUETA_JUSTIFICANTE[s.justificanteEstado][1]}</span>
                    : s.tieneJustificante && <span className="chip chip-muted">Justificante</span>}
                  {s.contrincanteAvisadoEn && <span className="chip chip-muted">Comunicado el {fechaCorta(s.contrincanteAvisadoEn)}</span>}
                  {!s.correoEnviado && <span className="chip chip-danger">Correo no enviado</span>}
                  {s.revisadaEn ? <span className="chip chip-success">Revisada</span> : <span className="chip chip-muted">Pendiente</span>}
                </span>
              </button>

              {abierta === s.id && (
                <div className="ra-detalle">
                  <p>
                    Partido ({s.estado}) · jornada del {formatearFecha(s.fechaJornada)}
                    {!s.admiteWo && s.fechaLimite && <> · plazo hasta {formatearFecha(s.fechaLimite)}</>}
                    {s.justificanteEstado === 'APROBADO' && <> · {s.fechaJuego ? `nueva fecha de juego ${formatearFecha(s.fechaJuego)}` : 'nueva fecha por fijar'}</>}
                  </p>
                  {s.errorEnvio && <p className="msg-error">Fallo del correo: {s.errorEnvio}</p>}
                  {s.contrincanteAvisoError && <p className="msg-error">Fallo del último aviso al contrincante: {s.contrincanteAvisoError}</p>}
                  {s.revisadaEn && <p className="mz-vacio">Revisada por {s.revisadaPor} el {fechaCorta(s.revisadaEn)}.</p>}

                  {s.tieneJustificante
                    ? <VisorJustificante solicitudId={s.id} nombre={s.justificanteNombre} tipo={s.justificanteTipo} />
                    : <p className="mz-vacio">Sin justificante.</p>}

                  {errorAccion && <p className="msg-error" role="alert">{errorAccion}</p>}
                  <div className="ra-acciones">
                    {s.justificanteEstado === 'PENDIENTE' && (
                      <>
                        <label className="ra-fecha">
                          Nueva fecha de juego (opcional)
                          <input type="date" value={fechaElegida} onChange={(e) => setFechaElegida(e.target.value)} />
                        </label>
                        <button type="button" className="btn btn-success btn-sm" disabled={trabajando}
                          onClick={() => ejecutar(aprobarJustificante, s.id, fechaElegida)}>
                          Aprobar justificante
                        </button>
                        <button type="button" className="btn btn-danger btn-sm" disabled={trabajando}
                          onClick={() => window.confirm('Se rechaza el justificante: el partido pasa a aplazado con el plazo de siempre (7 días desde la jornada). ¿Continuar?') && ejecutar(rechazarJustificante, s.id)}>
                          Rechazar
                        </button>
                      </>
                    )}
                    {s.justificanteEstado === 'APROBADO' && s.estado === 'APLAZADO' && (
                      <>
                        <label className="ra-fecha">
                          {s.fechaJuego ? 'Reprogramar' : 'Fecha de juego'}
                          <input type="date" value={fechaElegida} onChange={(e) => setFechaElegida(e.target.value)} />
                        </label>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={trabajando || !fechaElegida}
                          onClick={() => ejecutar(fijarFechaJuego, s.id, fechaElegida)}>
                          Guardar fecha
                        </button>
                      </>
                    )}
                    {s.estado === 'APLAZADO' && (
                      <button type="button" className="btn btn-primary btn-sm" disabled={trabajando} onClick={() => setAvisando(s.id)}>
                        {s.contrincanteAvisadoEn ? `Comunicado el ${fechaCorta(s.contrincanteAvisadoEn)} · reenviar` : 'Comunicar'}
                      </button>
                    )}
                    {s.justificanteEstado === 'RECHAZADO' && s.estado === 'APLAZADO' && s.fechaLimite && s.fechaLimite < hoyISO() && (
                      <button type="button" className="btn btn-danger btn-sm" disabled={trabajando}
                        onClick={() => window.confirm('El plazo venció tras rechazar el justificante. Se propondrá la incomparecencia (2-0 para el rival). Se confirma después en Actas. ¿Continuar?') && ejecutar(aplicarWo, s.id)}>
                        Aplicar incomparecencia
                      </button>
                    )}
                    {s.admiteWo && s.estado === 'PENDIENTE' && !s.revisadaEn && (
                      <button type="button" className="btn btn-danger btn-sm" disabled={trabajando}
                        onClick={() => window.confirm('Se propondrá la incomparecencia (2-0 para el rival). Se confirma después en Actas. ¿Continuar?') && ejecutar(aplicarWo, s.id)}>
                        Aplicar incomparecencia
                      </button>
                    )}
                    {!s.revisadaEn && s.justificanteEstado !== 'PENDIENTE' && (
                      <button type="button" className="btn btn-success btn-sm" disabled={trabajando} onClick={() => ejecutar(marcarRevisada, s.id)}>
                        Marcar como revisada
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {avisando !== null && (
        <ModalComunicar solicitudId={avisando} onCerrar={() => setAvisando(null)} onEnviado={cargar} />
      )}
    </div>
  );
}
